import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { auth, onAuthStateChanged } from '../../../../auth';
import convex, { waitForConvexAuthReady } from '../../../../convexClient';
import { api } from '../../../../convex/_generated/api';
import { SHORT_CACHE_TTL } from '../../../../utils/cacheConfig';
import { withActiveTenant } from '../../../../utils/tenantUtils';

const CACHE_EXPIRATION_TIME = SHORT_CACHE_TTL;

// Module-level flag — synchronously prevents concurrent callers from each
// creating their own watchQuery before the first one has had a chance to
// store the unsubscribe handle in Zustand state.
let _accountSubscriptionSetupInProgress = false;

let authReadyPromise = null;
function ensureUser() {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      const unsub = onAuthStateChanged(auth, (u) => { unsub(); resolve(u || null); });
    });
  }
  return authReadyPromise;
}

const useAccountStore = create(
  persist(
    (set, get) => ({
      accounts: [],
      accountGroups: [],
      accountGroupCache: {},
      loading: false,
      error: null,
      lastFetchedAccounts: null,
      lastFetchedAccountGroups: null,
      ledgerSignals: {},
      lastFetchedLedgerSignals: null,

      // Real-time subscription unsubscribe handle (Convex watchQuery)
      _unsubscribeAccounts: null,

      setAccounts: (accounts) => set({ accounts, lastFetchedAccounts: Date.now() }),
      setAccountGroups: (accountGroups) => set({ accountGroups, lastFetchedAccountGroups: Date.now() }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
      resetSession: () => {
        const unsubscribeAccounts = get()._unsubscribeAccounts;
        if (typeof unsubscribeAccounts === "function") {
          unsubscribeAccounts();
        }

        set({
          accounts: [],
          accountGroups: [],
          accountGroupCache: {},
          loading: false,
          error: null,
          lastFetchedAccounts: null,
          lastFetchedAccountGroups: null,
          ledgerSignals: {},
          lastFetchedLedgerSignals: null,
          _unsubscribeAccounts: null,
        });
      },

      fetchAccountGroups: async () => {
        const user = await ensureUser();
        if (!user) return;

        const lastFetchTime = get().lastFetchedAccountGroups;
        if (lastFetchTime && Date.now() - lastFetchTime < CACHE_EXPIRATION_TIME) return;

        set({ loading: true, error: null });
        try {
          await waitForConvexAuthReady();
          const groups = await convex.query(
            api.categories.listAccountsGroups,
            {}
          );

          const cache = { ...(get().accountGroupCache || {}) };
          groups.forEach((g) => { cache[g._id] = g; });

          set({
            accountGroups: groups,
            accountGroupCache: cache,
            lastFetchedAccountGroups: Date.now(),
          });
        } catch (err) {
          set({ error: err.message });
        } finally {
          set({ loading: false });
        }
      },

      /**
       * Fetches per-account ledger-vs-stored balance drift + savingsDesignated
       * (sub-project 4b). One-shot query (not a live subscription, unlike
       * fetchAccounts) — drift is a diagnostic signal, not something that
       * needs sub-second freshness — cached for CACHE_EXPIRATION_TIME same
       * as fetchAccountGroups, with a `force` escape hatch for right after a
       * Reconcile action clears the drift.
       */
      fetchAccountLedgerSignals: async ({ force = false } = {}) => {
        const user = await ensureUser();
        if (!user) return;

        const lastFetchTime = get().lastFetchedLedgerSignals;
        if (!force && lastFetchTime && Date.now() - lastFetchTime < CACHE_EXPIRATION_TIME) return;

        set({ error: null });
        try {
          await waitForConvexAuthReady();
          const signals = await convex.query(
            api.transactions.getAccountLedgerSignals,
            withActiveTenant()
          );
          set({ ledgerSignals: signals || {}, lastFetchedLedgerSignals: Date.now() });
        } catch (err) {
          set({ error: err.message });
        }
      },

      /**
       * Subscribes to real-time account updates via Convex watchQuery.
       * Replaces Firestore onSnapshot listeners.
       */
      fetchAccounts: async (filter = 'All') => {
        // Synchronous guards — must both pass before doing any async work.
        // Checking Zustand state alone is not enough: multiple callers can all
        // read `null` from the store before any of them has set a value.
        // The module-level flag is set synchronously at call time, so only one
        // caller can proceed; all subsequent concurrent callers return immediately.
        if (_accountSubscriptionSetupInProgress) return;
        const prev = get()._unsubscribeAccounts;
        if (typeof prev === 'function') return;

        _accountSubscriptionSetupInProgress = true;

        const user = await ensureUser();
        if (!user) {
          _accountSubscriptionSetupInProgress = false;
          set({ accounts: [], loading: false, _unsubscribeAccounts: null });
          return;
        }

        set({ loading: true, error: null });

        try {
          await waitForConvexAuthReady();
          const watch = convex.watchQuery(api.accounts.listAccounts, {
            status: filter !== 'All' ? filter : undefined,
            ...withActiveTenant(),
          });

          const unsub = watch.onUpdate(() => {
            let result;
            try {
              result = watch.localQueryResult();
            } catch (err) {
              set({ error: err.message, loading: false });
              return;
            }
            if (result === undefined) return; // still loading
            set({ accounts: result || [], lastFetchedAccounts: Date.now(), loading: false });

            // Populate account group cache from results
            const { accountGroupCache, lastFetchedAccountGroups } = get();
            const hasUncachedGroups = (result || [])
              .some((a) => {
                const id = a.type || a.accountGroupId || a.groupId;
                return id && !accountGroupCache[id];
              });

            if (hasUncachedGroups || !lastFetchedAccountGroups) {
              convex
                .query(api.categories.listAccountsGroups, {})
                .then((groups) => {
                  const cache = { ...get().accountGroupCache };
                  groups.forEach((g) => { cache[g._id] = g; });
                  set({ accountGroupCache: cache, accountGroups: groups, lastFetchedAccountGroups: Date.now() });
                })
                .catch(() => {});
            }
          });

          set({ _unsubscribeAccounts: unsub });
        } catch (err) {
          set({ error: err.message, loading: false });
        } finally {
          // Always clear the in-progress flag so a retry can succeed if this
          // attempt failed before storing the unsubscribe handle.
          _accountSubscriptionSetupInProgress = false;
        }
      },

      deleteAccount: async (accountId) => {
        const user = await ensureUser();
        if (!user || !accountId) return;
        set({ loading: true, error: null });
        try {
          await convex.mutation(api.accounts.deleteAccount, { id: accountId });
        } catch (err) {
          set({ error: err.message });
        } finally {
          set({ loading: false });
        }
      },

      updateAccountBalance: async (accountId, newBalance) => {
        const user = await ensureUser();
        if (!user || !accountId) return;
        set({ loading: true, error: null });
        try {
          await convex.mutation(api.accounts.updateBalance, { id: accountId, balance: newBalance });
          const accounts = get().accounts.map((acc) =>
            acc.id === accountId ? { ...acc, balance: newBalance } : acc
          );
          set({ accounts });
        } catch (err) {
          set({ error: err.message });
        } finally {
          set({ loading: false });
        }
      },

      getAccountById: (accountId) => get().accounts.find((acc) => acc.id === accountId),
      getAllAccounts: () => get().accounts,
    }),
    {
      name: 'account-store',
      partialize: (state) => ({
        accounts: state.accounts,
        accountGroups: state.accountGroups,
        accountGroupCache: state.accountGroupCache,
        lastFetchedAccounts: state.lastFetchedAccounts,
        lastFetchedAccountGroups: state.lastFetchedAccountGroups,
      }),
    }
  )
);

export default useAccountStore;
